"use client";

import type { ReactNode } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { MenuHunterMark } from "@/components/brand/MenuHunterMark";
import {
  LayoutDashboard,
  UtensilsCrossed,
  CreditCard,
  Users,
  QrCode,
  User,
} from "lucide-react";
import { capturePostHogEvent } from "@/lib/posthog-client";
import { POSTHOG_EVENTS, POSTHOG_SOURCE } from "@/lib/posthog-events";
import { useEffect } from "react";
import { useLanguage } from "@/lib/useLanguage";
import { LanguageToggle } from "@/components/LanguageToggle";
import { ThemeToggle } from "@/components/ThemeToggle";

const NAV_HREFS = [
  { href: "/owner/home", key: "dashboard" as const, icon: LayoutDashboard },
  { href: "/owner/menus", key: "menus" as const, icon: UtensilsCrossed },
  { href: "/owner/billing", key: "billing" as const, icon: CreditCard },
  { href: "/owner/team", key: "team" as const, icon: Users },
  { href: "/owner/qrcode", key: "qrcode" as const, icon: QrCode },
  { href: "/owner/profile", key: "profile" as const, icon: User },
];

export function OwnerShell({ children }: { children: ReactNode }) {
  const pathname = usePathname() ?? "";
  const { t } = useLanguage();
  const rp = t.restaurantPanel;
  const goHome = t.common?.goHome ?? "Ir al inicio";

  useEffect(() => {
    if (pathname.startsWith("/owner")) {
      capturePostHogEvent(POSTHOG_EVENTS.OWNER_DASHBOARD_VIEWED, { source: POSTHOG_SOURCE });
    }
  }, [pathname]);

  return (
    <div className="min-h-dvh flex flex-col md:flex-row bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      <aside className="md:w-56 shrink-0 border-b md:border-b-0 md:border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="flex items-center justify-between gap-2 p-4 border-b border-slate-200 dark:border-slate-800 md:border-b-0">
          <Link
            href="/owner/home"
            className="flex items-center gap-2 font-semibold text-slate-900 dark:text-slate-50 min-w-0"
          >
            <MenuHunterMark size={24} strokeWidth={15.5} variant="micro" className="shrink-0" />
            <span className="truncate text-sm">{rp.shellTitle}</span>
          </Link>
          <div className="flex items-center gap-1 shrink-0 md:hidden">
            <LanguageToggle />
            <ThemeToggle />
          </div>
        </div>
        <nav className="flex md:flex-col overflow-x-auto md:overflow-visible px-2 pb-2 md:pb-4 gap-1">
          {NAV_HREFS.map(({ href, key, icon: Icon }) => {
            const active = pathname === href || pathname.startsWith(`${href}/`);
            return (
              <Link
                key={href}
                href={href}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${
                  active
                    ? "bg-accent/15 text-slate-900 dark:text-accent font-medium"
                    : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" aria-hidden />
                {rp.nav[key]}
              </Link>
            );
          })}
        </nav>
        <div className="hidden md:flex items-center gap-2 px-4 pb-4">
          <LanguageToggle />
          <ThemeToggle />
        </div>
        <div className="hidden md:block px-4 pb-4">
          <Link
            href="/explore"
            className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
          >
            ← {goHome}
          </Link>
        </div>
      </aside>
      <main className="flex-1 min-w-0 p-4 md:p-8 max-w-4xl">{children}</main>
    </div>
  );
}
